<template>
  <div class="public-profile">
    <PublicProfileHeader
      :active-tab="0"
      @user-loaded="onUserLoaded"
      @connections-loaded="onConnectionsLoaded"
    />

    <!-- Profile Content -->
    <template v-if="userLoaded">
      <!-- Tab Content -->
      <v-container class="profile-body">
        <v-row>
          <!-- Left Sidebar -->
          <v-col
            cols="12"
            md="5"
          >
            <!-- About Card -->
            <v-card class="mb-4 profile-card">
              <v-card-title class="font-weight-bold">
                About
              </v-card-title>
              <v-card-text>
                <p
                  v-if="user.bio"
                  class="bio-text mb-4"
                >
                  {{ user.bio }}
                </p>
                <p
                  v-else
                  class="text--secondary"
                >
                  No bio available.
                </p>
                <div class="about-details">
                  <div
                    v-if="user.username"
                    class="detail-row"
                  >
                    <v-icon class="detail-icon">
                      {{ mdiAccountCircle }}
                    </v-icon>
                    <span>@{{ user.username }}</span>
                  </div>
                  <div
                    v-if="user.created_at"
                    class="detail-row"
                  >
                    <v-icon class="detail-icon">
                      {{ mdiCalendar }}
                    </v-icon>
                    <span>Member since {{ formatDate(user.created_at) }}</span>
                  </div>
                </div>
              </v-card-text>
            </v-card>

            <!-- Connections Card -->
            <v-card class="mb-4 profile-card">
              <v-card-title class="d-flex justify-space-between align-center">
                <span class="font-weight-bold">Connections</span>
              </v-card-title>
              <v-card-subtitle v-if="connectionsCount > 0">
                {{ connectionsCount }} connection{{ connectionsCount !== 1 ? 's' : '' }}
              </v-card-subtitle>
              <v-card-text>
                <v-row
                  v-if="connections.length"
                  dense
                >
                  <v-col
                    v-for="(connection, idx) in connections.slice(0, 9)"
                    :key="`sidebar-conn-${idx}`"
                    cols="4"
                    class="text-center connection-item"
                    @click="goToProfile(connection.username)"
                  >
                    <v-avatar
                      size="80"
                      class="mb-1 rounded-lg cursor-pointer"
                      tile
                    >
                      <v-img
                        :src="connection.photo || defaultAvatar"
                        :alt="connection.name"
                      ></v-img>
                    </v-avatar>
                    <div class="connection-name text-body-2">
                      {{ connection.name }}
                    </div>
                  </v-col>
                </v-row>
                <p
                  v-else
                  class="text--secondary"
                >
                  No connections yet.
                </p>
              </v-card-text>
            </v-card>

            <!-- Photos Preview Card -->
            <v-card class="mb-4 profile-card">
              <v-card-title class="d-flex justify-space-between align-center">
                <span class="font-weight-bold">Pictures</span>
                <span
                  v-if="photos.length"
                  class="text-body-2 primary--text cursor-pointer"
                  @click="$router.push({ name: 'public_pictures', params: { username: $route.params.username } })"
                >
                  See all pictures
                </span>
              </v-card-title>
              <v-card-text>
                <!-- Loading skeleton for photos -->
                <v-row
                  v-if="contentLoading"
                  dense
                >
                  <v-col
                    v-for="n in 9"
                    :key="`photo-skeleton-${n}`"
                    cols="4"
                  >
                    <v-skeleton-loader
                      type="image"
                      class="rounded"
                    ></v-skeleton-loader>
                  </v-col>
                </v-row>
                <v-row
                  v-else-if="photos.length"
                  dense
                >
                  <v-col
                    v-for="(photo, idx) in photos.slice(0, 9)"
                    :key="`photo-preview-${idx}`"
                    cols="4"
                  >
                    <v-img
                      :src="photo.url"
                      aspect-ratio="1"
                      class="rounded photo-thumb"
                      @click="openPhotoModal(photo)"
                    >
                      <template v-slot:placeholder>
                        <v-row
                          class="fill-height ma-0"
                          align="center"
                          justify="center"
                        >
                          <v-progress-circular
                            indeterminate
                            color="grey lighten-1"
                          ></v-progress-circular>
                        </v-row>
                      </template>
                    </v-img>
                  </v-col>
                </v-row>
                <p
                  v-else-if="!contentLoading"
                  class="text--secondary"
                >
                  No public pictures.
                </p>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- Right Content -->
          <v-col
            cols="12"
            md="7"
          >
            <!-- Biography Preview Card -->
            <v-card class="mb-4 profile-card">
              <v-card-title class="d-flex justify-space-between align-center">
                <span class="font-weight-bold">Biography</span>
                <span
                  v-if="biographyEntries.length"
                  class="text-body-2 primary--text cursor-pointer"
                  @click="$router.push({ name: 'public_biography', params: { username: $route.params.username } })"
                >
                  See full biography
                </span>
              </v-card-title>
              <v-card-text>
                <!-- Loading skeleton for biography -->
                <template v-if="contentLoading">
                  <div
                    v-for="n in 3"
                    :key="`bio-skeleton-${n}`"
                    class="mb-4"
                  >
                    <div class="d-flex align-center mb-3">
                      <v-skeleton-loader
                        type="avatar"
                        class="me-3"
                      ></v-skeleton-loader>
                      <div style="flex: 1">
                        <v-skeleton-loader type="text" width="40%"></v-skeleton-loader>
                        <v-skeleton-loader type="text" width="25%"></v-skeleton-loader>
                      </div>
                    </div>
                    <v-skeleton-loader type="paragraph"></v-skeleton-loader>
                    <v-skeleton-loader type="image" class="mt-2 rounded"></v-skeleton-loader>
                    <v-divider
                      v-if="n < 3"
                      class="mt-4"
                    ></v-divider>
                  </div>
                </template>
                <template v-else-if="biographyEntries.length">
                  <div
                    v-for="(entry, idx) in visibleBiographyEntries"
                    :key="`bio-preview-${idx}`"
                    class="biography-preview-entry"
                  >
                    <div class="bio-preview-content">
                      <div class="d-flex align-center mb-2">
                        <v-avatar
                          size="40"
                          class="me-3"
                        >
                          <v-img
                            :src="avatarUrl"
                            :alt="user.name"
                          ></v-img>
                        </v-avatar>
                        <div>
                          <div class="font-weight-medium">
                            {{ user.name }}
                          </div>
                          <div class="text-caption text--secondary">
                            {{ formatDate(entry.created_at) }}
                          </div>
                        </div>
                      </div>
                      <h3
                        v-if="entry.name"
                        class="text-subtitle-1 font-weight-bold mb-1"
                      >
                        {{ entry.name }}
                      </h3>
                      <p
                        v-if="entry.alternative_text"
                        class="text-body-2 mb-2"
                        v-html="truncateText(entry.alternative_text, 200)"
                      ></p>
                      <template v-if="entry.related_files && entry.related_files.length">
                        <v-img
                          v-if="isImage(entry.related_files[0])"
                          :src="entry.related_files[0]"
                          class="rounded mt-2 bio-entry-image"
                          width="100%"
                          @error="onImgError(entry.related_files[0])"
                        ></v-img>
                        <video
                          v-else-if="isVideo(entry.related_files[0])"
                          width="100%"
                          controls
                          class="rounded mt-2"
                        >
                          <source
                            :src="entry.related_files[0]"
                            :type="getVideoType(entry.related_files[0])"
                          >
                        </video>
                      </template>
                      <!-- Like Button -->
                      <div class="like-row mt-3">
                        <v-icon
                          :color="likeStatus[entry.id] ? 'red' : ''"
                          size="20"
                          class="cursor-pointer"
                          @click="toggleLike(entry)"
                        >
                          {{ likeStatus[entry.id] ? mdiHeart : mdiHeartOutline }}
                        </v-icon>
                        <span
                          v-if="likeCounts[entry.id]"
                          class="like-count cursor-pointer"
                          @click="openLikersDialog(entry.id)"
                        >{{ likeCounts[entry.id] }}</span>
                      </div>
                    </div>
                    <v-divider
                      v-if="idx < visibleBiographyEntries.length - 1"
                    ></v-divider>
                  </div>
                  <!-- Load More Button -->
                  <div
                    v-if="biographyEntries.length > biographyLimit"
                    class="text-center mt-4"
                  >
                    <v-btn
                      outlined
                      color="primary"
                      @click="loadMoreBiography"
                    >
                      Load more
                    </v-btn>
                  </div>
                </template>
                <p
                  v-else-if="!contentLoading"
                  class="text--secondary"
                >
                  No public biography entries.
                </p>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </template>

    <!-- Photo Modal -->
    <v-dialog
      v-model="photoModal"
      max-width="900"
    >
      <v-card v-if="selectedPhoto">
        <v-img
          :src="selectedPhoto.url"
          contain
          max-height="80vh"
        ></v-img>
        <v-card-text v-if="selectedPhoto.name">
          <h3 class="mt-2">
            {{ selectedPhoto.name }}
          </h3>
          <p
            v-if="selectedPhoto.alternative_text"
            v-html="selectedPhoto.alternative_text"
          ></p>
        </v-card-text>
        <v-card-actions>
          <div
            v-if="selectedPhoto.id"
            class="like-action"
            @click="toggleLike(selectedPhoto)"
          >
            <v-icon
              :color="likeStatus[selectedPhoto.id] ? 'red' : ''"
              size="20"
            >
              {{ likeStatus[selectedPhoto.id] ? mdiHeart : mdiHeartOutline }}
            </v-icon>
            <span
              v-if="likeCounts[selectedPhoto.id]"
              class="like-count"
            >{{ likeCounts[selectedPhoto.id] }}</span>
          </div>
          <div
            v-if="selectedPhoto.id"
            class="comment-action d-flex align-center ml-4"
          >
            <v-icon size="20">
              {{ mdiCommentOutline }}
            </v-icon>
            <span
              v-if="modalCommentCount > 0"
              class="like-count ml-1"
            >{{ modalCommentCount }}</span>
          </div>
          <v-spacer></v-spacer>
          <v-btn
            text
            @click="photoModal = false"
          >
            Close
          </v-btn>
        </v-card-actions>
        <CommentsSection
          v-if="selectedPhoto && selectedPhoto.id"
          :file-id="selectedPhoto.id"
          @count-updated="modalCommentCount = $event"
        />
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from 'axios'
import {
  mdiAccountCircle,
  mdiCalendar,
  mdiHeartOutline,
  mdiHeart,
  mdiCommentOutline,
} from '@mdi/js'
import PublicProfileHeader from '@/components/PublicProfileHeader.vue'
import LikersDialog from '@/components/LikersDialog.vue'
import CommentsSection from '@/components/CommentsSection.vue'

export default {
  name: 'PublicProfile',
  components: {
    PublicProfileHeader,
    LikersDialog,
    CommentsSection,
  },
  data: () => ({
    mdiAccountCircle,
    mdiCalendar,
    mdiHeartOutline,
    mdiHeart,
    mdiCommentOutline,

    userLoaded: false,
    contentLoading: false,
    biographyLimit: 3,
    photoModal: false,
    selectedPhoto: null,
    modalCommentCount: 0,

    user: {
      name: '',
      username: '',
      bio: '',
      avatar: '',
      created_at: '',
    },

    connections: [],
    connectionsCount: 0,
    photos: [],
    biographyEntries: [],
    failedImages: {},
    likeStatus: {},
    likeCounts: {},
    likersDialogOpen: false,
    likersDialogFileId: null,

    endpoint: process.env.VUE_APP_ROOT_API,
    defaultAvatar: require('@/assets/images/avatars/1.png'),
  }),
  computed: {
    avatarUrl() {
      if (this.user.avatar) {
        return `https://self-biography-system.s3.us-east-2.amazonaws.com/${this.user.avatar}`
      }

      return this.defaultAvatar
    },
    visibleBiographyEntries() {
      return this.biographyEntries.slice(0, this.biographyLimit)
    },
  },
  methods: {
    onUserLoaded(userData) {
      this.user = userData
      this.userLoaded = true
      this.contentLoading = true
      this.loadPublicContent(this.$route.params.username)
    },

    onConnectionsLoaded(data) {
      this.connections = data.connections.map(c => ({
        ...c,
        photo: c.avatar
          ? `https://self-biography-system.s3.us-east-2.amazonaws.com/${c.avatar}`
          : this.defaultAvatar,
      }))
      this.connectionsCount = data.count
    },

    async loadPublicContent(username) {
      try {
        const response = await axios.get(
          `${this.endpoint}/getPublicItems?username=${encodeURIComponent(username)}`,
        )

        if (response.data.content) {
          const allItems = response.data.content

          this.photos = []
          this.biographyEntries = []

          allItems.forEach(item => {
            this.biographyEntries.push(item)

            if (item.related_files && item.related_files.length) {
              item.related_files.forEach(file => {
                if (this.isImage(file) && !this.isVideo(file)) {
                  this.photos.push({
                    id: item.id,
                    url: file,
                    name: item.name,
                    alternative_text: item.alternative_text,
                  })
                }
              })
            }
          })

          // Fetch like data
          this.fetchLikeData(allItems.map(i => i.id))
        }
      } catch {
        // Content loading failed silently
      }
      this.contentLoading = false
    },

    loadMoreBiography() {
      this.biographyLimit += 5
    },

    goToProfile(username) {
      if (username) {
        this.$router.push({ name: 'public_user', params: { username } })
      }
    },

    openPhotoModal(photo) {
      this.selectedPhoto = photo
      this.modalCommentCount = 0
      this.photoModal = true
    },

    async fetchLikeData(fileIds) {
      if (!fileIds || !fileIds.length) return
      try {
        const token = this.$store.state.jwtToken
        if (token) {
          const response = await axios.get(
            `${this.endpoint}/getLikeStatus?file_ids=${fileIds.join(',')}`,
            { headers: { Authorization: token } },
          )
          if (response.data) {
            this.likeStatus = { ...this.likeStatus, ...response.data.likeStatus }
            this.likeCounts = { ...this.likeCounts, ...response.data.likeCounts }
          }
        } else {
          // Unauthenticated - fetch counts only via getLikes for each file
          for (const id of fileIds) {
            try {
              const response = await axios.get(`${this.endpoint}/getLikes?file_id=${id}`)
              if (response.data) {
                this.$set(this.likeCounts, id, response.data.likeCount || 0)
              }
            } catch {
              // skip
            }
          }
        }
      } catch {
        // Silently fail
      }
    },

    async toggleLike(entry) {
      if (!this.$store.state.jwtToken) return
      try {
        const response = await axios.post(`${this.endpoint}/toggleLike`, {
          file_id: entry.id,
        }, {
          headers: { Authorization: this.$store.state.jwtToken },
        })
        if (response.data) {
          this.$set(this.likeStatus, entry.id, response.data.liked)
          this.$set(this.likeCounts, entry.id, response.data.likeCount)
        }
      } catch {
        // Silently fail
      }
    },
    openLikersDialog(fileId) {
      this.likersDialogFileId = fileId
      this.likersDialogOpen = true
    },

    formatDate(dateStr) {
      if (!dateStr) return ''
      const date = new Date(dateStr)
      const options = { year: 'numeric', month: 'long', day: 'numeric' }

      return date.toLocaleDateString('en-US', options)
    },

    truncateText(text, maxLength) {
      if (!text) return ''
      if (text.length <= maxLength) return text

      return `${text.substring(0, maxLength)}...`
    },

    getFileExtension(filePath) {
      if (!filePath) return ''
      try {
        const pathname = new URL(filePath).pathname

        return pathname.split('.').pop().toLowerCase()
      } catch {
        return filePath.split('.').pop().split('?')[0].toLowerCase()
      }
    },

    isImage(filePath) {
      if (!filePath) return false
      const ext = this.getFileExtension(filePath)
      const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'tiff', 'svg', 'heic', 'heif', 'ico']
      const videoExtensions = ['mp4', 'avi', 'mov', 'mkv', 'webm', 'flv', 'wmv', 'mpg', 'mpeg']
      if (imageExtensions.includes(ext)) return true
      if (videoExtensions.includes(ext)) return false

      return false
    },

    isVideo(filePath) {
      if (!filePath) return false
      const ext = this.getFileExtension(filePath)
      const videoExtensions = ['mp4', 'avi', 'mov', 'mkv', 'webm', 'flv', 'wmv', 'mpg', 'mpeg']
      if (videoExtensions.includes(ext)) return true

      return !!this.failedImages[filePath]
    },

    onImgError(filePath) {
      this.$set(this.failedImages, filePath, true)
    },

    getVideoType(filePath) {
      const ext = this.getFileExtension(filePath)
      const types = {
        mp4: 'video/mp4',
        webm: 'video/webm',
        avi: 'video/avi',
        mov: 'video/quicktime',
        mkv: 'video/x-matroska',
        flv: 'video/x-flv',
        wmv: 'video/x-ms-wmv',
        mpg: 'video/mpeg',
        mpeg: 'video/mpeg',
      }

      return types[ext] || 'video/mp4'
    },
  },
}
</script>

<style lang="scss" scoped>
.public-profile {
  min-height: 100vh;
}

/* Body Content */
.profile-body {
  padding-top: 24px;
}

.profile-card {
  border-radius: 8px;
}

.bio-text {
  font-size: 0.95rem;
  line-height: 1.5;
  white-space: pre-line;
}

/* About Details */
.about-details {
  .detail-row {
    display: flex;
    align-items: center;
    padding: 8px 0;

    .detail-icon {
      margin-right: 12px;
      opacity: 0.7;
    }
  }
}

/* Connections */
.connection-item {
  cursor: pointer;

  .connection-name {
    font-weight: 500;
    line-height: 1.2;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

/* Photos */
.photo-thumb {
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.85;
  }
}

/* Biography Preview (All tab) */
.biography-preview-entry {
  position: relative;
  padding: 16px 0;

  .bio-preview-content {
    padding-bottom: 16px;
  }

  .bio-entry-image {
    width: 100%;

    ::v-deep .v-image__image {
      background-size: cover;
    }
  }

  .like-row {
    display: flex;
    align-items: center;
  }
}

/* Cursor utility */
.cursor-pointer {
  cursor: pointer;
}

/* Like action */
.like-action {
  display: inline-flex;
  align-items: center;
  cursor: pointer;

  .v-icon {
    transition: transform 0.2s;
  }

  .like-count {
    font-size: 13px;
    margin-left: 4px;
    font-weight: 500;
  }
}

.comment-action {
  .like-count {
    font-size: 13px;
    font-weight: 500;
  }
}
</style>
